import { useState } from "react";
import { Reveal, SectionLabel } from "./Reveal";
import {
  Brain, CalendarCheck, Clock, Check,
  PhoneIncoming, PhoneOutgoing, MessageSquare, MessageCircle,
  Instagram, Facebook, Globe, Mic,
} from "lucide-react";

const MEMORY_POINTS = [
  { icon: Brain, title: "Remembers every conversation", desc: "Across calls, texts, and chats — one shared memory." },
  { icon: CalendarCheck, title: "Knows your calendar", desc: "Understands if a booking is upcoming or already done." },
  { icon: Clock, title: "Picks up where it left off", desc: "A week or a year later, the conversation just continues." },
];

const CHANNELS = [
  { icon: PhoneIncoming, name: "Inbound Calls", desc: "Answers every call, qualifies, and books.", span: "lg:col-span-2" },
  { icon: PhoneOutgoing, name: "Outbound Calls", desc: "Re-engages leads and runs callbacks." },
  { icon: MessageSquare, name: "SMS Texts", desc: "Two-way text follow-ups, 24/7." },
  { icon: MessageCircle, name: "WhatsApp", desc: "Real conversations on the world's #1 app." },
  { icon: Instagram, name: "Instagram DMs", desc: "Replies to story mentions and DMs instantly." },
  { icon: Facebook, name: "Facebook Messenger", desc: "Captures leads from your ads in real time.", span: "lg:col-span-2" },
  { icon: Globe, name: "Website Chat", desc: "A friendly chat bubble for any website." },
  { icon: Mic, name: "Voice Widget", desc: "A floating orb visitors can talk to, not type." },
];

export const Features = () => {
  const [on, setOn] = useState(true);

  return (
    <>
      {/* Memory feature */}
      <section id="features" data-testid="memory-section" className="relative py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            <Reveal className="lg:col-span-5">
              <SectionLabel>The feature nobody else has</SectionLabel>
              <h2 className="font-heading mt-4 text-3xl md:text-5xl font-bold tracking-tight text-white leading-tight">
                One brain.<br />Every channel.
              </h2>
              <p className="mt-5 text-base md:text-lg text-white/60 leading-relaxed">
                Whether a customer called last week or texted today, 5B AI remembers
                the whole story. It never asks the same question twice — just smooth,
                professional service that feels personal every time.
              </p>
              <div className="mt-8 space-y-5">
                {MEMORY_POINTS.map((p) => (
                  <div key={p.title} className="flex gap-4">
                    <span className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl border border-brand/30 bg-brand/10">
                      <p.icon className="h-5 w-5 text-brand" />
                    </span>
                    <div>
                      <div className="font-semibold text-white">{p.title}</div>
                      <div className="text-sm text-white/55 mt-0.5">{p.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </Reveal>

            <Reveal delay={0.15} className="lg:col-span-7">
              <div className="relative rounded-2xl border border-white/10 bg-[#0c0c0c] p-8 md:p-10 overflow-hidden">
                <div className="absolute -right-20 -top-20 h-64 w-64 rounded-full bg-brand/15 blur-[90px]" />
                <div className="relative flex items-center justify-between mb-8">
                  <div>
                    <div className="text-xs uppercase tracking-widest text-white/40">Memory</div>
                    <div className="font-heading text-lg font-semibold text-white">Flip it on. It just remembers.</div>
                  </div>
                  <button
                    data-testid="memory-toggle"
                    onClick={() => setOn((v) => !v)}
                    className={`relative h-8 w-14 rounded-full transition-colors duration-300 ${on ? "bg-brand" : "bg-white/15"}`}
                  >
                    <span className={`absolute top-1 h-6 w-6 rounded-full bg-white transition-all duration-300 ${on ? "left-7" : "left-1"}`} />
                  </button>
                </div>

                <div className={`grid grid-cols-3 gap-3 transition-opacity duration-500 ${on ? "opacity-100" : "opacity-30"}`}>
                  {["Voice", "SMS", "WhatsApp", "Instagram", "Web Chat", "Facebook"].map((c, i) => (
                    <div key={c} className="rounded-xl border border-white/10 bg-white/[0.03] p-4 text-center">
                      <div className="text-sm font-medium text-white">{c}</div>
                      <div className="mt-2 flex items-center justify-center gap-1 text-[11px] text-emerald-400">
                        <Check className="h-3 w-3" /> synced
                      </div>
                    </div>
                  ))}
                </div>
                <p className="relative mt-6 text-sm text-white/45">
                  Competitors don't offer this at all. For 5B AI, it's a single switch —
                  no extra tools, no complicated setup, no per-channel rebuilds.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Channels grid */}
      <section id="channels" data-testid="channels-section" className="relative py-24 md:py-32 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal className="text-center max-w-2xl mx-auto">
            <SectionLabel>Every surface, one brain</SectionLabel>
            <h2 className="font-heading mt-4 text-3xl md:text-5xl font-bold tracking-tight text-white">
              8 channels. 1 conversation.
            </h2>
            <p className="mt-5 text-base md:text-lg text-white/60">
              Every call, text, and message writes into the same memory.
              Your AI never asks a customer the same question twice.
            </p>
          </Reveal>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {CHANNELS.map((c, i) => (
              <Reveal key={c.name} delay={(i % 4) * 0.06} className={c.span || ""}>
                <div className="group h-full rounded-2xl border border-white/10 bg-[#0c0c0c] p-6 transition-all duration-300 hover:-translate-y-1 hover:border-brand/40 hover:shadow-[0_8px_32px_rgba(255,92,0,0.15)]">
                  <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-white/5 border border-white/10 transition-colors group-hover:bg-brand/10 group-hover:border-brand/30">
                    <c.icon className="h-5 w-5 text-white/70 transition-colors group-hover:text-brand" />
                  </span>
                  <h3 className="mt-5 font-heading text-lg font-semibold text-white">{c.name}</h3>
                  <p className="mt-1.5 text-sm text-white/50">{c.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};
